// src/features/dashboard/components/EmployeeIdleTime/EmployeeIdleHeader.tsx

import { EmployeeIdleKpiResponse } from '../../types/Employee/employeeIdleTime.types';
import { useDashboardDate } from '../../context/DashboardDateContext';

interface Props {
  kpi?: EmployeeIdleKpiResponse | null;
}

// Target = 1h 30m = 90 minutes
const TARGET_IDLE_MINUTES = 90;

/**
 * Format API time
 *
 * 00:18:00 -> 18m
 * 01:18:00 -> 1h 18m
 */
const formatIdleTime = (
  value?: string | null
): string => {
  if (!value) {
    return '--';
  }

  const parts = value.split(':');

  if (parts.length !== 3) {
    return value;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return value;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

/**
 * Parse calendar date without UTC timezone conversion.
 *
 * Supports:
 * 2026-08-13
 * 2026-08-13T00:00:00
 * Date
 */
const parseCalendarDate = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    return new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate()
    );
  }

  // Handle YYYY-MM-DD / YYYY-MM-DDTHH:mm:ss
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})/
  );

  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);

    return new Date(year, month, day);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
};

/**
 * Format date as:
 * 13 Aug 2026
 */
const formatFullDate = (date: Date): string => {
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Get date difference.
 *
 *  0  = Today
 * -1  = Yesterday
 */
const getDayDifference = (
  selectedDate: Date,
  today: Date
): number => {
  const selected = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate()
  );

  const current = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const millisecondsPerDay =
    24 * 60 * 60 * 1000;

  return Math.round(
    (selected.getTime() - current.getTime()) /
      millisecondsPerDay
  );
};

/**
 * Calendar date label:
 *
 * Today 13 Aug 2026
 * Yesterday 12 Aug 2026
 * 11 Aug 2026
 */
const getCalendarDateLabel = (
  value: string | Date | null | undefined
): string => {
  const selectedDate = parseCalendarDate(value);

  if (!selectedDate) {
    const today = new Date();

    return `Today ${formatFullDate(today)}`;
  }

  const today = new Date();

  const difference = getDayDifference(
    selectedDate,
    today
  );

  const fullDate = formatFullDate(
    selectedDate
  );

  if (difference === 0) {
    return `Today ${fullDate}`;
  }

  if (difference === -1) {
    return `Yesterday ${fullDate}`;
  }

  return fullDate;
};

/**
 * Format target:
 *
 * 90 -> 1h 30m
 */
const formatTargetTime = (
  minutes: number
): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${remainingMinutes}m`;
};

const EmployeeIdleHeader = ({
  kpi,
}: Props) => {
  const { fromDate } = useDashboardDate();

  const employeeName =
    sessionStorage.getItem('employeeName') ||
    localStorage.getItem('employeeName') ||
    'Employee';

  const businessUnit =
    sessionStorage.getItem('businessUnit') ||
    localStorage.getItem('businessUnit') ||
    '';

  const dateLabel = getCalendarDateLabel(
    fromDate
  );

  const idleTime = formatIdleTime(
    kpi?.idleToday
  );

  /**
   * Compare with last week.
   *
   * API:
   * idleTodayPercentageChange
   */
  const idleChange =
    kpi?.idleTodayPercentageChange ?? 0;

  const lastWeekText =
    idleChange === 0
      ? 'Same as last week'
      : `${idleChange > 0 ? '+' : ''}${idleChange.toFixed(
          1
        )}% vs last week`;

  return (
    <div
      className="
        mb-4
        flex
        flex-wrap
        items-center
        gap-3
      "
    >
      {/* Title */}

      <h1
        className="
          text-[16px]
          font-semibold
          leading-none
          text-[#002B5B]
        "
      >
        Idle Time
      </h1>

      {/* Separator */}

      <span
        className="
          text-[16px]
          font-medium
          leading-none
          text-[#002B5B]
        "
      >
        ·
      </span>

      {/* Selected Date + Idle Time */}

      <span
        className="
          text-[16px]
          font-semibold
          leading-none
          text-[#002B5B]
        "
      >
        {dateLabel} {idleTime}
      </span>

      {/* Employee / Target / Last Week */}

      <div
        className="
          ml-2
          flex
          items-center
          rounded-full
          bg-[#EEF4FF]
          px-4
          py-2
          text-[12px]
          font-medium
          text-[#3538CD]
        "
      >
        {/* Employee */}

        <span>
          {employeeName}
        </span>

        {/* Business Unit */}

        {businessUnit && (
          <>
            <span className="mx-1">
              ·
            </span>

            <span>
              {businessUnit}
            </span>
          </>
        )}

        {/* Target */}

        <span className="mx-1">
          ·
        </span>

        <span>
          Target ≤ {formatTargetTime(TARGET_IDLE_MINUTES)} per day
        </span>

        {/* Last Week */}

        <span className="mx-1">
          ·
        </span>

        <span>
          {lastWeekText}
        </span>
      </div>
    </div>
  );
};

export default EmployeeIdleHeader;